/**
 * AI Personalized Recommendations
 *
 * 1. Reads browsing signals from CustomerBehavior (localStorage).
 * 2. Collects real candidate products from the store (collections, search, recommendations API).
 * 3. Asks the recommendation API (Vercel proxy → Gemini) to pick and explain the best matches.
 * 4. Renders them with the global product card, showing a skeleton while loading.
 *
 * One AI request returns a pool of up to `poolSize` products (cached). Each page view
 * shows the next window of `maxProducts` from that pool, so products rotate without
 * extra AI calls. Layout is either a grid or a scroll-snap slider with arrows/autoplay.
 *
 * The section stays hidden for visitors without any browsing history.
 */

import { CustomerBehavior } from '@theme/customer-behavior';
import { hydrateGlobalCardsInRoot } from '@theme/global-product-card';

const CACHE_KEY = 'kptll:ai-reco:v1';
const ROTATION_KEY = 'kptll:ai-reco:rotation';
const FALLBACK_CACHE_MS = 30 * 60 * 1000;
const MAX_CANDIDATES = 60;

/**
 * @typedef {{ id: string, handle: string, title: string, vendor: string, type: string, price: number, tags: string[], match: string, unlisted: boolean }} Candidate
 * @typedef {{ headline: string, subheading: string, items: { id: string, handle: string, reason: string }[], ai: boolean }} RecoResult
 */

/** @param {string} text */
function hash(text) {
  let h = 0;
  for (let i = 0; i < text.length; i++) h = (Math.imul(31, h) + text.charCodeAt(i)) | 0;
  return String(h >>> 0);
}

/** @param {unknown} tags */
function normalizeTags(tags) {
  const list = Array.isArray(tags) ? tags : String(tags || '').split(',');
  return list
    .map((tag) => String(tag).trim())
    .filter(Boolean)
    .slice(0, 5);
}

/** @param {unknown} tags */
function isUnlisted(tags) {
  const list = Array.isArray(tags) ? tags : String(tags || '').split(',');
  return list.some((tag) => String(tag).trim().toLowerCase() === 'unlisted');
}

/** @param {unknown} price */
function normalizePrice(price) {
  // Recommendations API returns cents as integers; products.json / suggest.json return strings in major units.
  if (typeof price === 'number') return price / 100;
  return Number.parseFloat(String(price || '0')) || 0;
}

class AiPersonalizedRecommendations extends HTMLElement {
  connectedCallback() {
    this.root = window.Shopify?.routes?.root || '/';
    this.maxProducts = Number(this.dataset.maxProducts) || 4;
    this.poolSize = Math.max(Number(this.dataset.poolSize) || this.maxProducts, this.maxProducts);
    this.cacheMs = (Number(this.dataset.cacheHours) || 6) * 60 * 60 * 1000;
    this.timeoutMs = (Number(this.dataset.timeoutSeconds) || 15) * 1000;
    this.designMode = Boolean(window.Shopify?.designMode);

    this.grid = this.querySelector('[data-ai-reco-grid]');
    this.headingEl = this.querySelector('[data-ai-reco-heading]');
    this.subheadingEl = this.querySelector('[data-ai-reco-subheading]');

    void this.load();
  }

  disconnectedCallback() {
    this.teardownSlider();
  }

  async load() {
    if (!CustomerBehavior.hasSignals()) {
      if (this.designMode) this.showEditorPreview();
      return;
    }

    const signals = CustomerBehavior.topSignals(5);
    const signature = hash(
      JSON.stringify([
        this.dataset.locale,
        this.poolSize,
        signals.collections.map((c) => c.key),
        signals.searches.map((s) => s.key),
        signals.products.map((p) => p.key),
        signals.filters.map((f) => f.key),
      ])
    );

    const cached = this.readCache(signature);
    if (cached) {
      this.hidden = false;
      await this.render(cached, signature);
      return;
    }

    this.hidden = false;
    this.setAttribute('aria-busy', 'true');

    try {
      const candidates = await this.fetchCandidates(signals);
      if (candidates.length === 0) return this.hide();

      /** @type {RecoResult | null} */
      let result = null;
      try {
        result = await this.askAi(signals, candidates);
      } catch (error) {
        console.warn('[AI Recommendations] AI request failed, using fallback.', error);
      }

      if (!result) {
        if (this.dataset.fallback !== 'true') return this.hide();
        result = this.fallbackResult(candidates);
      } else if (this.dataset.fallback === 'true') {
        // The AI may return fewer products than requested; top up the pool with the remaining candidates.
        result.items = this.fillPool(result.items, candidates);
      }

      if (result.items.length === 0) return this.hide();

      this.writeCache(signature, result);
      await this.render(result, signature);
    } catch (error) {
      console.warn('[AI Recommendations]', error);
      this.hide();
    } finally {
      this.removeAttribute('aria-busy');
    }
  }

  hide() {
    if (this.designMode) return;
    this.hidden = true;
  }

  showEditorPreview() {
    this.hidden = false;
    this.querySelector('[data-ai-reco-editor-note]')?.removeAttribute('hidden');
  }

  /* ---------------------------------------------------------------- Candidates */

  /**
   * @param {ReturnType<typeof CustomerBehavior.topSignals>} signals
   * @returns {Promise<Candidate[]>}
   */
  async fetchCandidates(signals) {
    /** @type {Promise<Candidate[]>[]} */
    const requests = [];

    for (const search of signals.searches.slice(0, 2)) {
      requests.push(this.fetchSearch(search.term));
    }
    for (const collection of signals.collections.slice(0, 2)) {
      requests.push(this.fetchCollection(collection.handle, collection.title));
    }
    for (const product of signals.products.slice(0, 2)) {
      requests.push(this.fetchRelated(product.id, product.title));
    }

    const results = await Promise.allSettled(requests);
    const viewed = new Set(signals.products.map((p) => p.key));

    /** @type {Map<string, Candidate>} */
    const unique = new Map();
    for (const result of results) {
      if (result.status !== 'fulfilled') continue;
      for (const candidate of result.value) {
        if (candidate.unlisted) continue;
        if (!unique.has(candidate.id)) unique.set(candidate.id, candidate);
      }
    }

    // Prefer products the customer has not opened yet, but keep viewed ones as backup.
    const list = [...unique.values()];
    const fresh = list.filter((c) => !viewed.has(c.id));
    const seen = list.filter((c) => viewed.has(c.id));
    return [...fresh, ...seen].slice(0, MAX_CANDIDATES);
  }

  /** @param {string} url */
  async fetchJson(url) {
    const response = await fetch(url, { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error(`${response.status} ${url}`);
    return response.json();
  }

  /** @param {string} term */
  async fetchSearch(term) {
    const params = new URLSearchParams({
      q: term,
      'resources[type]': 'product',
      'resources[limit]': '10',
      'resources[options][unavailable_products]': 'hide',
    });
    const data = await this.fetchJson(`${this.root}search/suggest.json?${params}`);
    const products = data?.resources?.results?.products || [];

    return products
      .filter((/** @type {any} */ p) => p.available !== false)
      .map((/** @type {any} */ p) => this.toCandidate(p, p.price, `search "${term}"`));
  }

  /**
   * @param {string} handle
   * @param {string} title
   */
  async fetchCollection(handle, title) {
    const data = await this.fetchJson(`${this.root}collections/${encodeURIComponent(handle)}/products.json?limit=30`);
    const products = data?.products || [];

    return products
      .filter((/** @type {any} */ p) => p.variants?.some((/** @type {any} */ v) => v.available))
      .map((/** @type {any} */ p) => {
        const variant = p.variants.find((/** @type {any} */ v) => v.available) || p.variants[0];
        return this.toCandidate(p, variant?.price, `collection "${title || handle}"`);
      });
  }

  /**
   * @param {string} productId
   * @param {string} title
   */
  async fetchRelated(productId, title) {
    const params = new URLSearchParams({ product_id: productId, limit: '8', intent: 'related' });
    const data = await this.fetchJson(`${this.root}recommendations/products.json?${params}`);
    const products = data?.products || [];

    return products
      .filter((/** @type {any} */ p) => p.available !== false)
      .map((/** @type {any} */ p) => this.toCandidate(p, p.price, `similar to "${title}"`));
  }

  /**
   * @param {any} product
   * @param {unknown} price
   * @param {string} match
   * @returns {Candidate}
   */
  toCandidate(product, price, match) {
    return {
      id: String(product.id),
      handle: product.handle,
      title: product.title,
      vendor: product.vendor || '',
      type: product.product_type || product.type || '',
      price: normalizePrice(price),
      tags: normalizeTags(product.tags),
      match,
      unlisted: isUnlisted(product.tags),
    };
  }

  /* ---------------------------------------------------------------- AI */

  /**
   * Sends the interests + candidates to the recommendation API (Vercel proxy),
   * which builds the prompt and calls Gemini with a server-side key.
   * @param {ReturnType<typeof CustomerBehavior.topSignals>} signals
   * @param {Candidate[]} candidates
   * @returns {Promise<RecoResult | null>}
   */
  async askAi(signals, candidates) {
    const apiUrl = this.dataset.apiUrl;
    if (!apiUrl) return null;

    const payload = {
      model: this.dataset.model,
      language: this.dataset.language,
      shopName: this.dataset.shopName,
      currency: this.dataset.currency,
      maxProducts: this.poolSize,
      interests: {
        searches: signals.searches.map((s) => ({ term: s.term, score: s.score })),
        collections: signals.collections.map((c) => ({ name: c.title || c.handle, score: c.score })),
        viewed_products: signals.products.map((p) => ({ title: p.title, type: p.type, vendor: p.vendor, score: p.score })),
        filters: signals.filters.map((f) => ({ filter: f.label, score: f.score })),
      },
      candidates: candidates.map((c) => ({
        id: c.id,
        title: c.title,
        vendor: c.vendor,
        type: c.type,
        price: c.price,
        tags: c.tags,
        found_via: c.match,
      })),
    };

    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), this.timeoutMs);
    /** @type {any} */
    let data;

    try {
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        signal: controller.signal,
      });
      if (!response.ok) throw new Error(`Recommendation API ${response.status}: ${await response.text()}`);
      data = await response.json();
    } finally {
      clearTimeout(timer);
    }

    const byId = new Map(candidates.map((c) => [c.id, c]));
    const items = (Array.isArray(data?.items) ? data.items : [])
      .map((/** @type {any} */ item) => ({ candidate: byId.get(String(item.id)), reason: String(item.reason || '') }))
      .filter((/** @type {any} */ item) => item.candidate)
      .slice(0, this.poolSize)
      .map((/** @type {any} */ item) => ({ id: item.candidate.id, handle: item.candidate.handle, reason: item.reason }));

    if (items.length === 0) return null;

    return {
      headline: String(data.headline || ''),
      subheading: String(data.subheading || ''),
      items,
      ai: true,
    };
  }

  /**
   * Non-AI ranking used when the AI is not configured or fails.
   * @param {Candidate[]} candidates
   * @returns {RecoResult}
   */
  fallbackResult(candidates) {
    return {
      headline: '',
      subheading: '',
      items: candidates.slice(0, this.poolSize).map((c) => ({ id: c.id, handle: c.handle, reason: '' })),
      ai: false,
    };
  }

  /**
   * @param {RecoResult['items']} items
   * @param {Candidate[]} candidates
   */
  fillPool(items, candidates) {
    const used = new Set(items.map((item) => item.id));
    const extra = candidates
      .filter((c) => !used.has(c.id))
      .map((c) => ({ id: c.id, handle: c.handle, reason: '' }));
    return [...items, ...extra].slice(0, this.poolSize);
  }

  /* ---------------------------------------------------------------- Cache */

  /** @param {string} signature */
  readCache(signature) {
    try {
      const cache = JSON.parse(localStorage.getItem(CACHE_KEY) || 'null');
      if (!cache || cache.signature !== signature) return null;

      const ttl = cache.result?.ai ? this.cacheMs : FALLBACK_CACHE_MS;
      if (Date.now() - cache.at > ttl) return null;

      return /** @type {RecoResult} */ (cache.result);
    } catch {
      return null;
    }
  }

  /**
   * @param {string} signature
   * @param {RecoResult} result
   */
  writeCache(signature, result) {
    try {
      localStorage.setItem(CACHE_KEY, JSON.stringify({ signature, at: Date.now(), result }));
    } catch {
      // ignore
    }
  }

  /* ---------------------------------------------------------------- Rotation */

  /**
   * Returns the pool starting at this page view's rotation offset and advances it for the next one.
   * @param {RecoResult['items']} items
   * @param {string} signature
   */
  rotateItems(items, signature) {
    if (this.dataset.rotate !== 'true' || items.length <= this.maxProducts) return items;

    let offset = 0;
    try {
      const state = JSON.parse(localStorage.getItem(ROTATION_KEY) || 'null');
      if (state && state.signature === signature) offset = Number(state.offset) || 0;
      localStorage.setItem(
        ROTATION_KEY,
        JSON.stringify({ signature, offset: (offset + this.maxProducts) % items.length })
      );
    } catch {
      // ignore
    }

    offset %= items.length;
    return [...items.slice(offset), ...items.slice(0, offset)];
  }

  /* ---------------------------------------------------------------- Render */

  /**
   * @param {RecoResult} result
   * @param {string} signature
   */
  async render(result, signature) {
    if (!this.grid) return;

    // Hydrate cards off-DOM so the skeleton is swapped for complete cards in one step.
    // Cards that fail to render (deleted / unpublished since cached) are replaced by the next products in the pool.
    const queue = this.rotateItems(result.items, signature);
    const staging = document.createElement('div');
    let cursor = 0;

    while (staging.children.length < this.maxProducts && cursor < queue.length) {
      const batch = queue.slice(cursor, cursor + this.maxProducts - staging.children.length);
      cursor += batch.length;

      const round = document.createElement('div');
      round.append(...batch.map((item) => this.createCell(item)));
      await hydrateGlobalCardsInRoot(round);

      for (const placeholder of round.querySelectorAll('[data-global-card-placeholder]:not([data-hydrated])')) {
        placeholder.closest('.ai-reco__item')?.remove();
      }
      staging.append(...round.children);
    }

    if (staging.children.length === 0) {
      try {
        localStorage.removeItem(CACHE_KEY);
      } catch {
        // ignore
      }
      return this.hide();
    }

    if (this.dataset.useAiHeading === 'true' && result.headline && this.headingEl) {
      this.headingEl.textContent = result.headline;
    }
    if (this.subheadingEl) {
      const subheading = this.dataset.useAiHeading === 'true' ? result.subheading : '';
      this.subheadingEl.textContent = subheading || this.subheadingEl.textContent || '';
      this.subheadingEl.hidden = !this.subheadingEl.textContent;
    }

    this.grid.replaceChildren(...staging.children);
    this.grid.removeAttribute('aria-busy');
    this.classList.add('is-loaded');

    if (this.dataset.layout === 'slider') this.setupSlider();
  }

  /** @param {RecoResult['items'][number]} item */
  createCell(item) {
    const cell = document.createElement('div');
    cell.className = 'ai-reco__item';

    const placeholder = document.createElement('div');
    placeholder.className = 'global-product-card-placeholder';
    placeholder.setAttribute('data-global-card-placeholder', '');
    placeholder.dataset.productId = item.id;
    placeholder.dataset.productUrl = `${this.root}products/${item.handle}`;
    placeholder.style.width = '100%';
    cell.append(placeholder);

    if (this.dataset.showReason === 'true' && item.reason) {
      const reason = document.createElement('p');
      reason.className = 'ai-reco__reason';
      reason.textContent = item.reason;
      cell.append(reason);
    }

    return cell;
  }

  /* ---------------------------------------------------------------- Slider */

  setupSlider() {
    const track = this.grid;
    if (!track || this.sliderAbort) return;

    const nav = /** @type {HTMLElement | null} */ (this.querySelector('[data-ai-reco-nav]'));
    const prev = /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-ai-reco-prev]'));
    const next = /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-ai-reco-next]'));

    this.sliderAbort = new AbortController();
    const { signal } = this.sliderAbort;

    const updateNav = () => {
      const overflowing = track.scrollWidth > track.clientWidth + 1;
      if (nav) nav.hidden = !overflowing;
    };

    prev?.addEventListener('click', () => this.slide(-1, true), { signal });
    next?.addEventListener('click', () => this.slide(1, true), { signal });
    track.addEventListener(
      'keydown',
      (event) => {
        if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
          event.preventDefault();
          this.slide(event.key === 'ArrowRight' ? 1 : -1, false);
        }
      },
      { signal }
    );

    this.resizeObserver = new ResizeObserver(updateNav);
    this.resizeObserver.observe(track);
    updateNav();

    this.setupAutoplay(track, signal);
  }

  /**
   * Scrolls the slider by one product (or one view), wrapping around at either end.
   * @param {1 | -1} direction
   * @param {boolean} byPage
   */
  slide(direction, byPage) {
    const track = this.grid;
    if (!track) return;

    const maxScroll = track.scrollWidth - track.clientWidth;
    if (maxScroll <= 1) return;

    const item = /** @type {HTMLElement | null} */ (track.firstElementChild);
    const gap = Number.parseFloat(getComputedStyle(track).columnGap) || 0;
    const step = byPage ? track.clientWidth + gap : (item?.offsetWidth || track.clientWidth) + gap;

    if (direction > 0 && track.scrollLeft >= maxScroll - 2) {
      track.scrollTo({ left: 0 });
    } else if (direction < 0 && track.scrollLeft <= 2) {
      track.scrollTo({ left: maxScroll });
    } else {
      track.scrollBy({ left: direction * step });
    }
  }

  /**
   * @param {HTMLElement} track
   * @param {AbortSignal} signal
   */
  setupAutoplay(track, signal) {
    if (this.dataset.autoplay !== 'true') return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const delay = (Number(this.dataset.autoplaySeconds) || 5) * 1000;
    let hovered = false;
    let focused = false;
    let visible = false;

    const sync = () => {
      const shouldRun = visible && !hovered && !focused && !document.hidden;
      if (shouldRun && !this.autoplayTimer) {
        this.autoplayTimer = window.setInterval(() => this.slide(1, false), delay);
      } else if (!shouldRun && this.autoplayTimer) {
        window.clearInterval(this.autoplayTimer);
        this.autoplayTimer = undefined;
      }
    };

    this.addEventListener('pointerenter', () => ((hovered = true), sync()), { signal });
    this.addEventListener('pointerleave', () => ((hovered = false), sync()), { signal });
    this.addEventListener('focusin', () => ((focused = true), sync()), { signal });
    this.addEventListener(
      'focusout',
      (event) => ((focused = this.contains(/** @type {Node | null} */ (event.relatedTarget))), sync()),
      { signal }
    );
    track.addEventListener('touchstart', () => ((hovered = true), sync()), { signal, passive: true });
    document.addEventListener('visibilitychange', sync, { signal });

    this.intersectionObserver = new IntersectionObserver(([entry]) => {
      visible = Boolean(entry?.isIntersecting);
      sync();
    });
    this.intersectionObserver.observe(track);
  }

  teardownSlider() {
    this.sliderAbort?.abort();
    this.sliderAbort = undefined;
    this.resizeObserver?.disconnect();
    this.intersectionObserver?.disconnect();
    if (this.autoplayTimer) window.clearInterval(this.autoplayTimer);
    this.autoplayTimer = undefined;
  }
}

if (!customElements.get('ai-personalized-recommendations')) {
  customElements.define('ai-personalized-recommendations', AiPersonalizedRecommendations);
}
